// src/services/TextThumbnailService.ts

/**
 * Generates a simplified thumbnail for text files showing just the filename
 * and the first few words of the first line or two
 * @param file Text file
 * @returns Data URL containing the thumbnail image
 */
export async function generateTextThumbnail(file: File): Promise<string> {
  try {
    const text = await file.text();
    
    const canvas = document.createElement('canvas');
    canvas.width = 124;
    canvas.height = 140;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, 124, 140);
    
    // Get filename for the header
    const fileName = file.name;
    let displayName = fileName;
    const nameParts = fileName.split('.');
    const ext = nameParts.pop() || '';
    const baseName = nameParts.join('.');
    const maxBaseLength = 8; // Allow fewer base characters to fit extension
    displayName = baseName.length > maxBaseLength
      ? baseName.substring(0, maxBaseLength) + '...' + (ext || '')
      : baseName + (ext ? '.' + ext : '');
    
    // Instead of showing TXT, show the filename at the top
    ctx.fillStyle = '#4285f4';
    ctx.fillRect(0, 0, 124, 25); // Make header slightly taller for larger text
    
    ctx.fillStyle = 'white';
    ctx.font = 'bold 14px Lucida Grande'; // Larger font for header
    ctx.textAlign = 'center';
    ctx.fillText(displayName, 62, 17); // Display filename instead of extension
    
    ctx.strokeStyle = '#e0e0e0';
    ctx.strokeRect(0, 0, 124, 140);
    
    // Show file content
    const processedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = processedText.split('\n').filter(line => line.trim().length > 0).slice(0, 3);
    
    ctx.fillStyle = '#444';
    ctx.font = '16px Lucida Grande'; // Use a monospaced font for better readability
    ctx.textAlign = 'left';
    
    lines.forEach((line, i) => {
      const words = line.split(' ').filter(word => word.trim().length > 0);
      const displayWords = words.slice(0, 4);
      let displayText = displayWords.join(' ');
      
      if (displayWords.length < words.length) {
        displayText += '...';
      }
      
      // Only show about 12 characters per line
      if (displayText.length > 12) {
        displayText = displayText.substring(0, 10) + '...';
      }
      
      const yPosition = 45 + (i * 22);
      ctx.fillText(displayText, 5, yPosition);
    });
    
    if (file.size) {
      const formatSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
      };
      
      ctx.fillStyle = '#555';
      ctx.font = '16px Arial'; // Even larger font for file size
      ctx.textAlign = 'center'; // Center align for better visibility
      ctx.fillText(formatSize(file.size), 50, 130); // Centered position
    }
    
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch (error) {
    console.error('Error creating text preview:', error);
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    
    canvas.width = 124;
    canvas.height = 140;
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, 124, 140);
    
    // Get filename for the header in fallback case
    const fileName = file.name;
    let displayName = fileName;
    const nameParts = fileName.split('.');
    const ext = nameParts.pop() || '';
    const baseName = nameParts.join('.');
    const maxBaseLength = 8; // Allow fewer base characters to fit extension
    displayName = baseName.length > maxBaseLength
      ? baseName.substring(0, maxBaseLength) + '...' + (ext || '')
      : baseName + (ext ? '.' + ext : '');
    
    // Show the filename in the header instead of extension
    ctx.fillStyle = '#4285f4';
    ctx.textAlign = 'center';
    ctx.fillRect(0, 0, 124, 25);
    
    ctx.fillStyle = 'white';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(displayName, 50, 17);
    
    ctx.strokeStyle = '#e0e0e0';
    ctx.strokeRect(0, 0, 124, 140);
    
    ctx.fillStyle = '#444';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Text file', 62, 60);
    
    // Display file size with larger font
    if (file.size) {
      const formatSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
      };
      
      ctx.fillStyle = '#555';
      ctx.font = '13px Arial'; // Even larger font for file size
      ctx.textAlign = 'center'; // Center align for better visibility
      ctx.fillText(formatSize(file.size), 50, 130); // Centered position
    }
    
    return canvas.toDataURL('image/jpeg', 0.9);
  }
}